import { makeStyles } from '@mui/styles';
import { muiTheme } from '@tassat/payments-themes';

const checkmarkContainerStyles = {
    top: '3px',
    left: '12px',
    width: '6px',
    height: '18px',
    borderWidth: '0 3px 3px 0',
    transform: 'rotate(45deg)',
    pointerEvents: 'none' as const
};

export default makeStyles((theme: typeof muiTheme) => ({
    field: {
        display: 'flex',
        width: '100%',
        flexFlow: 'column nowrap',
        marginBottom: '1rem',
        '& input:focus-visible': { outline: `auto 1px ${theme?.app?.clientTheme.colors.primaryColor || '#375073'}` }
    },
    noMarginBottom: { marginBottom: 0 },
    iconBackground: {
        padding: '0 .5rem',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center' as const,
        width: '2.3rem',
        justifyContent: 'center' as const
    },
    iconSize: { height: '1.2rem' },
    viewButtonStyle: {
        position: 'absolute' as const,
        right: '4px',
        height: '2.75rem',
        '&:hover': { backgroundColor: 'transparent' as const }
    },
    inputContainer: {
        position: 'relative' as const,
        display: 'inline-flex' as const
    },
    loginInputContainer: {
        position: 'relative' as const,
        display: 'flex',
        border: '1px solid hsl(0,0%,80%)',
        borderRadius: '3px'
    },
    inputLabel: {
        color: theme?.app?.applicationColors.enumColors.grey || '#4a4a4a',
        fontSize: '.8rem',
        marginBottom: '.3rem',
        fontFamily: 'corporate-s,sans-serif'
    },
    inputLabelLarge: {
        color: theme?.app?.clientTheme.colors.primaryColor || '#375073',
        fontSize: '1rem',
        fontWeight: 'bold' as const,
        marginBottom: '.6rem',
        fontFamily: 'corporate-s,sans-serif'
    },
    textInput: {
        color: theme?.app?.applicationColors.enumColors.greyDark || '#333',
        height: '2.75rem',
        width: '100%',
        fontFamily: 'corporate-s,sans-serif',
        fontSize: '14px',
        padding: '0 .45rem',
        border: `1px solid ${theme?.app?.applicationColors.enumColors.greyLighter || '#cfcfcf'}`,
        borderRadius: '3px'
    },
    loginTextInput: {
        color: theme?.app?.applicationColors.enumColors.greyDark || '#333',
        height: '2.75rem',
        width: '100%',
        fontFamily: 'corporate-s,sans-serif',
        fontSize: '14px',
        padding: '0 .45rem',
        border: '0',
        borderRadius: '3px'
    },
    textInputLarge: {
        color: theme?.app?.applicationColors.enumColors.greyDark || '#333',
        height: '3.25rem',
        width: '100%',
        fontFamily: 'corporate-s,sans-serif',
        fontSize: '1.5rem',
        padding: '0 .25rem',
        border: 'none' as const,
        borderBottom: `1px solid ${theme?.app?.applicationColors.enumColors.greyLighter || '#cfcfcf'}`
    },
    currencyInputLarge: {
        color: theme?.app?.applicationColors.enumColors.greyDark || '#333',
        height: '3.25rem',
        width: '100%',
        fontFamily: 'corporate-s,sans-serif',
        fontSize: '2.75rem',
        padding: '0 .25rem',
        border: 'none' as const,
        borderBottom: `1px solid ${theme?.app?.applicationColors.enumColors.greyLighter || '#cfcfcf'}`
    },
    checkboxInput: {
        height: '2.75rem',
        width: '3.5rem',
        backgroundColor: theme?.app?.applicationColors.backgroundPrimary || '#ffffff',
        margin: '0'
    },
    select: {
        color: theme?.app?.applicationColors.enumColors.greyLight || '#949494',
        minHeight: '46px',
        fontSize: '14px',
        fontFamily: 'corporate-s,sans-serif'
    },
    currencyPrefix: {
        fontSize: '1.75rem',
        paddingTop: '.3rem'
    },
    fieldError: {
        minHeight: '20px',
        height: 'auto',
        color: theme?.app?.applicationColors.enumColors.red || '#f00',
        fontSize: '11px',
        minWidth: '65px'
    },
    emptyError: {
        minHeight: '20px',
        height: 'auto'
    },
    clientRoleButtonsContainer: { minHeight: '36px' },
    clientRoleField: { marginRight: '.75rem' },
    radioField: {
        display: 'flex',
        width: '100%',
        flexFlow: 'column nowrap',
        marginBottom: '1.5rem'
    },
    radioContainer: {
        display: 'flex',
        flexFlow: 'row wrap',
        alignItems: 'center'
    },
    radioOptionContainer: {
        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
        paddingRight: '2rem'
    },
    radioLabel: { fontSize: '1.15rem' },
    radioInput: {
        width: '28px',
        height: '28px',
        marginRight: '.5rem'
    },
    checkmarkContainer: {
        position: 'relative' as const,
        display: 'flex',
        flexFlow: 'column nowrap',
        fontSize: '22px',
        userSelect: 'none' as const,
        width: 'fit-content',
        '& input': {
            opacity: 0,
            position: 'absolute' as const,
            top: 0,
            left: 0,
            height: '30px',
            width: '30px',
            cursor: 'pointer' as const
        },
        '&:hover input ~ .checkmark': { backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter || '#CFCFCF' },
        '&:hover input:disabled ~ .checkmark': { backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter || '#CFCFCF' },
        '& input:checked ~ .checkmark': { backgroundColor: theme?.app?.clientTheme.colors.primaryColor || '#375073' },
        '& input:disabled': { cursor: 'default' as const },
        '& input:disabled ~ .checkmark': { backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter || '#CFCFCF' },
        '& input:checked ~ .checkmark:after': { display: 'flex' },
        '& .checkmark:after': {
            ...checkmarkContainerStyles,
            border: `1px solid ${theme?.app?.applicationColors.enumColors.white || '#FFFFFF'}`
        },
        '& input:checked:disabled ~ .checkmark:after': {
            ...checkmarkContainerStyles,
            border: `1px solid ${theme?.app?.applicationColors.enumColors.grey || '#888888'}`
        }
    },
    checkmark: {
        height: '30px',
        width: '30px',
        backgroundColor: theme?.app?.applicationColors.enumColors.white || '#FFFFFF',
        border: `1px solid ${theme?.app?.applicationColors.enumColors.greyLighter || '#CFCFCF'}`,
        borderRadius: '3px',
        '&:after': {
            content: '""',
            position: 'absolute' as const,
            display: 'none' as const
        }
    },
    toggleInput: {
        height: 0,
        width: 0,
        visibility: 'hidden' as const,
        display: 'none' as const,
        '&:checked + .toggleSwitchContainer .toggleButton': {
            left: 'calc(100% + 1px)',
            transform: 'translateX(-100%)'
        }
    },
    toggleSwitchContainerSmall: {
        display: 'flex',
        alignItems: 'center' as const,
        justifyContent: 'space-between' as const,
        cursor: 'pointer' as const,
        width: '62px',
        height: '28px',
        backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter || '#CFCFCF',
        border: `1px solid ${theme?.app?.applicationColors.enumColors.greyLighter || '#CFCFCF'}`,
        borderRadius: '100px',
        position: 'relative' as const,
        transition: 'background-color .2s'
    },
    toggleSwitchContainerSmallEnabled: { '&:active .toggleButton': { width: '36px' }},
    toggleButtonSmall: {
        position: 'absolute' as const,
        top: '-1px',
        left: '-1px',
        width: '30px',
        height: '30px',
        borderRadius: '100%',
        transition: '0.2s',
        backgroundColor: theme?.app?.applicationColors.enumColors.white || '#FFFFFF',
        boxShadow: '0 0 2px 0 rgba(10, 10, 10, 0.4)'
    },
    toggleSwitchContainer: {
        display: 'flex',
        alignItems: 'center' as const,
        justifyContent: 'space-between' as const,
        cursor: 'pointer' as const,
        width: '60px',
        height: '26px',
        backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter || '#CFCFCF',
        border: `1px solid ${theme?.app?.applicationColors.enumColors.greyLighter || '#CFCFCF'}`,
        borderRadius: '100px',
        position: 'relative' as const,
        transition: 'background-color .2s'
    },
    toggleSwitchContainerEnabled: { '&:active .toggleButton': { width: '36px' }},
    toggleButton: {
        position: 'absolute' as const,
        top: '-1px',
        left: '-1px',
        width: '28px',
        height: '28px',
        borderRadius: '100%',
        transition: '0.2s',
        backgroundColor: theme?.app?.applicationColors.enumColors.white || '#FFFFFF',
        boxShadow: '0 0 2px 0 rgba(10, 10, 10, 0.4)'
    },
    toggleActive: { backgroundColor: theme?.app?.clientTheme.colors.primaryColor || '#375073' },
    toggleButtonActive: { backgroundColor: theme?.app?.applicationColors.enumColors.white || '#FFFFFF' },
    toggleDisabled: {
        backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter || '#CFCFCF',
        opacity: '75%',
        cursor: 'default' as const
    },
    toggleButtonDisabled: { backgroundColor: theme?.app?.applicationColors.enumColors.white || '#FFFFFF' },
    toggleActiveDisabled: {
        backgroundColor: theme?.app?.clientTheme.colors.primaryColor || '#375073',
        opacity: '75%',
        cursor: 'default' as const
    },
    toggleButtonActiveDisabled: { backgroundColor: theme?.app?.applicationColors.enumColors.white || '#FFFFFF' },
    textInputConfirmationContainer: { maxWidth: '400px' },
    textInputConfirmationLabel: {
        fontSize: '14px',
        color: theme?.app?.applicationColors.enumColors.greyLight || '#a7a7a7',
        paddingBottom: '.75rem'
    },
    sliderInput: {
        width: '100%',
        maxWidth: '600px',
        cursor: 'pointer' as const,
        '-webkit-appearance': 'none' as const,
        backgroundColor: 'transparent' as const,
        '&::-webkit-slider-runnable-track': {
            height: '3px',
            backgroundColor: theme?.app?.clientTheme.colors.primaryColor || '#375073'
        },
        '&::-webkit-slider-thumb': {
            '-webkit-appearance': 'none' as const,
            width: 0,
            height: 0,
            marginTop: '2px',
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: `16px solid ${theme?.app?.clientTheme.colors.primaryColorLight || '#009BDF'}`
        }
    },
    selectAssetsOption: {
        padding: '0.15rem',
        lineHeight: '0.85rem',
        fontSize: '14px'
    },
    selectAssetsOptionCheckSelected: { marginRight: '0.25rem' },

    selectAssetsOptionCheck: {
        appearance: 'none' as const,
        'background-color': theme?.app?.applicationColors.enumColors.white || '#FFFFFF',
        border: `1px solid ${theme?.app?.applicationColors.enumColors.greyLight || '#a7a7a7'}`,
        padding: '9px',
        'border-radius': '3px',
        display: 'inline-block' as const,
        position: 'relative' as const,
        '&:active, &:checked': { 'box-shadow': '0 1px 2px rgba(0,0,0,0.05), inset 0px 1px 3px rgba(0,0,0,0.1)' },
        '&:checked': {
            'background-color': theme?.app?.applicationColors.enumColors.white || '#FFFFFF',
            border: `1px solid ${theme?.app?.applicationColors.enumColors.greyLight || '#a7a7a7'}`,
            'box-shadow': '0 1px 2px rgba(0,0,0,0.05), inset 0px -15px 10px -12px rgba(0,0,0,0.05), inset 15px 10px -12px rgba(255,255,255,0.1)',
            color: theme?.app?.applicationColors.enumColors.greyLight || '#a7a7a7'
        },
        '&:checked, &:after': { 'background-color': theme?.app?.clientTheme.colors.primaryColor || '#375074' }
    },

    selectAssetsMenuList: {
        marginTop: '0.5rem',
        maxHeight: '120px',
        overflowY: 'auto' as 'auto'
    },
    selectAssetsMenu: {
        maxHeight: '186px',
        borderRadius: '4px',
        boxShadow: '0 0 0 1px hsla(0,0%,0%,0.1), 0 4px 11px hsla(0,0%,0%,0.1)',
        padding: '0.6rem 0.5rem',
        backgroundColor: theme?.app?.applicationColors.enumColors.white || '#FFFFFF'
    },
    selectAssetsMenuButtonContainer: { marginBottom: '0.5rem' },
    selectAssetsMenuSelectAllButton: {
        fontSize: '0.85rem',
        display: 'flex' as const,
        'align-items': 'baseline'
    },
    selectAssetsMenuSelectAllCheckbox: {
        appearance: 'none' as const,
        'background-color': theme?.app?.applicationColors.enumColors.white || '#FFFFFF',
        border: `1px solid ${theme?.app?.applicationColors.enumColors.greyLight || '#a7a7a7'}`,
        padding: '5px',
        'border-radius': '3px',
        display: 'flex' as const,
        position: 'relative' as const,
        'align-self': 'center',
        'justify-content': 'center',
        '&:active, &:checked': { 'box-shadow': '0 1px 2px rgba(0,0,0,0.05), inset 0px 1px 3px rgba(0,0,0,0.1)' },
        '&:checked': {
            'background-color': theme?.app?.applicationColors.enumColors.white || '#FFFFFF',
            border: `1px solid ${theme?.app?.applicationColors.enumColors.greyLight || '#a7a7a7'}`,
            'box-shadow': '0 1px 2px rgba(0,0,0,0.05), inset 0px -15px 10px -12px rgba(0,0,0,0.05), inset 15px 10px -12px rgba(255,255,255,0.1)',
            color: theme?.app?.applicationColors.enumColors.greyLight || '#a7a7a7'
        },
        '&:checked, &:after': { 'background-color': theme?.app?.clientTheme.colors.primaryColor || '#375074' }
    },
    selectAssetsMenuClearButton: {
        marginLeft: '0.2rem',
        padding: '0.1rem',
        backgroundColor: 'transparent',
        border: 'none' as 'none',
        color: 'inherit',
        textDecoration: 'underline',
        fontSize: '0.85rem'
    },
    selectAssetsMenuApplyButton: {
        marginLeft: '0.2rem',
        marginRight: '0.5rem',
        padding: '0.15rem 0rem',
        fontSize: '0.85rem'
    }
}));